import { NgTemplateOutlet } from '@angular/common';
import { Component, TemplateRef, inject, input, output } from '@angular/core';

import { LocalizedLabelService } from '../../../core/services/utils/localized-label.service';

/** Un droit qu'on coche : sa clé et ses libellés. */
export interface PickerItem {
  key: string;
  labelFr: string;
  labelAr: string;
  labelEn: string;
}

/** Un groupe de droits, présenté sous un même en-tête. */
export interface PickerModule<T extends PickerItem = PickerItem> {
  id: string;
  labelFr: string;
  labelAr: string;
  labelEn: string;
  permissions: T[];
}

/**
 * Le sélecteur de droits par module : un en-tête par module (case de groupe,
 * compte « 3 / 6 »), puis ses droits à cocher, avec leur clé.
 *
 * <p>Partagé par « Profils et accès », qui y compose les permissions d'un
 * profil, et par « Offres et tarifs », qui y choisit ce qu'un pack ouvre à
 * l'élève : un même geste, un même rendu.</p>
 *
 * <p>Il affiche et signale, il ne décide pas. Chaque écran garde ses règles —
 * un droit verrouillé, ce que coche une case de module — et répond aux
 * événements `toggled` et `moduleToggled` ; l'état coché lui revient par
 * `selected`.</p>
 */
@Component({
  selector: 'app-permission-picker',
  standalone: true,
  imports: [NgTemplateOutlet],
  templateUrl: './permission-picker.component.html',
  styleUrl: './permission-picker.component.css',
})
export class PermissionPickerComponent<T extends PickerItem = PickerItem> {
  private readonly labels = inject(LocalizedLabelService);

  readonly modules = input.required<PickerModule<T>[]>();
  readonly selected = input.required<ReadonlySet<string>>();
  readonly readOnly = input(false);

  /** Un droit qu'on ne peut pas cocher ici, avec la raison au survol. */
  readonly locked = input<(item: T) => boolean>(() => false);
  readonly lockedTitle = input<string | null>(null);

  /** Ce qu'un écran ajoute sous le libellé d'un droit — pastilles, repères. */
  readonly extras = input<TemplateRef<{ $implicit: T }> | null>(null);

  readonly toggled = output<string>();
  readonly moduleToggled = output<PickerModule<T>>();

  label(item: PickerItem | PickerModule): string {
    return this.labels.pickPrefixed(item as unknown as Record<string, unknown>, 'label');
  }

  count(m: PickerModule<T>): number {
    const selected = this.selected();
    return m.permissions.filter((p) => selected.has(p.key)).length;
  }

  isLocked(item: T): boolean {
    return this.locked()(item);
  }
}
